import "./ClientStyles.css";

import React, { useState } from "react";
import {
  allPass,
  chain,
  cond,
  equals,
  filter,
  last,
  length,
  mergeDeepRight,
  path,
  pipe,
  propEq,
  propSatisfies,
} from "ramda";

import Chat from "./WidgetInterface/Chat/Chat";
import { CornerBackButton } from "./Elements/Buttons";
import Footer from "./WidgetInterface/Footer/Footer";
import { GoogleMap } from "./WidgetInterface/GoogleMap";
import Header from "./WidgetInterface/Header/Header";
import Results from "./WidgetInterface/Results/ResultsList";
import Tags from "./WidgetInterface/Tags/Tags";
import { createUseStyles } from "react-jss";
import doHttpRequest from "../lib/HTTPRequest";
import { eventTypes } from "../lib/analytics";
import keyframes from "../styles/Keyframes";
import { makeStylesFromThemeAndCustomStyles } from "../lib/StyleFunctions";
import { openLinksByLocation } from "../lib/utils";

const makeStyles = (theme) =>
  mergeDeepRight(
    {
      widgetInterface: {
        position: "fixed",
        maxHeight: "100%",
        bottom: "20px",
        right: "20px",
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        transition: "all 0.5s linear",
        borderRadius: "5px",
        boxShadow: "none",
        backgroundColor: "transparent",
        zIndex: "2147483647",
        animationName: "$slide-up",
        animationDuration: "0.2s",
        animationTimingFunction: "ease",
        "&.fullScreen": { height: "100%" },
        "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
          {
            borderRadius: "0",
            bottom: "0 !important",
            right: "0 !important",
            maxHeight: "100% !important",
            height: "100% !important",
            width: "100%",
          },
      },
      sideWrapper: {
        width: "460px",
        maxHeight: "850px",
        height: "88vh",
        borderRadius: "10px",
        boxShadow: "0 5px 40px rgba(0, 0, 0, 0.25) !important",
        "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
          {
            maxHeight: "100% !important",
            height: "100vh",
            borderRadius: "0 !important",
            width: "100% !important",
          },
      },
      rightSideWrapper: {
        composes: "$sideWrapper",
        boxShadow: "0 5px 40px rgba(0, 0, 0, 0.25) !important",
        overflowY: "hidden",
        display: "flex !important",
        flexDirection: "column",
      },
      leftSideWrapper: {
        composes: "$sideWrapper",
        boxShadow: "0 5px 40px rgba(0, 0, 0, 0.25) !important",
        marginRight: "13px",
        position: "relative",
        borderRadius: "5px",
        overflowX: "hidden",
        display: "none",
        backgroundColor: theme.lightWhite,
        animationName: "$close-side-window",
        animationDuration: "0.2s",
        animationTimingFunction: "ease-in-out",
        animationFillMode: "forwards",
        "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
          {
            margin: "0 !important",
          },
      },
      leftOpen: {
        display: "block",
        animationName: "$open-side-window",
        animationDuration: "0.2s",
        animationTimingFunction: "ease-in-out",
        animationFillMode: "forwards",
      },
      leftIsOpen: {
        "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
          {
            display: "none !important",
          },
      },
    },
    keyframes
  );

const handleSuggestionClick =
  ({ sendEvent, sendUtteranceSuggestion }) =>
  (suggestion) => {
    sendEvent(eventTypes.SUGGESTION_CLICK, suggestion);
    if (suggestion.phone || suggestion.fax) {
      window.location.href = "tel:" + (suggestion.phone || suggestion.fax);
      return;
    }
    if (suggestion.url) {
      openLinksByLocation(suggestion.url);
      return;
    }
    if (suggestion.httpRequest) {
      doHttpRequest(suggestion.httpRequest, sendEvent);
      return;
    }
    if (suggestion.sayText) {
      sendUtteranceSuggestion(suggestion.sayText);
      return;
    }
    sendUtteranceSuggestion(suggestion.displayText);
  };

const resultToBotMessage = (result) => ({
  // TODO(uri): Very strange.
  message: result,
  type: eventTypes.BOT_UTTERANCE,
});

const botUtteranceEventToMessages = ({
  data: { text, suggestions, results },
}) => [
  {
    message: {
      text,
    },
    type: eventTypes.BOT_UTTERANCE,
    suggestions,
    results,
  },
  ...(length(results) === 1 ? [resultToBotMessage(results[0])] : []),
];

const userUtteranceEventToMessages = ({ data: { utterance } }) => [
  {
    message: {
      text: utterance,
    },
    type: eventTypes.USER_UTTERANCE,
  },
];

const checkIfAgentMessage = allPass([
  propEq("type", eventTypes.BOT_UTTERANCE),
  pipe(path(["data", "sender"]), equals("liveAgent")),
]);

const eventToMessages = ({ data, type }) => {
  const messages = [];
  const handler = cond([
    [
      propEq("type", eventTypes.USER_UTTERANCE),
      ({ data, type }) =>
        messages.push({
          message: {
            text: data.utterance,
          },
          type,
        }),
    ],
    [
      checkIfAgentMessage,
      ({ data }) =>
        messages.push({
          message: {
            text: data.text,
          },
          type: eventTypes.AGENT_UTTERANCE,
        }),
    ],
    [
      propEq("type", eventTypes.BOT_UTTERANCE),
      ({ data, type }) => {
        messages.push({
          message: {
            text: data.text,
          },
          type,
          suggestions: data.suggestions,
          results: data.results,
        });
        if (data.results && data.results.length === 1) {
          messages.push({
            message: data.results[0],
            type,
          });
        }
      },
    ],
  ]);

  handler({ data, type });
  return messages;
};

const shouldDisplayEvent = propSatisfies(
  (type) =>
    [eventTypes.BOT_UTTERANCE, eventTypes.USER_UTTERANCE].includes(type),
  "type"
);

const historyToMessages = pipe(
  filter(shouldDisplayEvent),
  chain(
    cond([
      [propEq("type", eventTypes.USER_UTTERANCE), userUtteranceEventToMessages],
      [propEq("type", eventTypes.BOT_UTTERANCE), botUtteranceEventToMessages],
    ])
  )
);

const LeftPanel = ({
  styles,
  mapResults,
  results,
  sendEvent,
  sendUtteranceSuggestion,
  setMapResults,
  setShowMapPanel,
  setShowResultsPanel,
  showMapPanel,
  showResultsPanel,
}) => (
  <div className={[styles.leftSideWrapper, styles.leftOpen].join(" ")}>
    {showMapPanel ? (
      mapResults.length ? (
        <GoogleMap
          state={showMapPanel}
          results={mapResults}
          sendEvent={sendEvent}
          onSuggestionClick={(suggestion) => {
            handleSuggestionClick({ sendEvent, sendUtteranceSuggestion })(
              suggestion
            );
            setShowMapPanel(false);
            setShowResultsPanel(false);
          }}
        />
      ) : null
    ) : results.length ? (
      <Results
        state={showResultsPanel}
        results={results}
        sendEvent={sendEvent}
        onAddressClick={(result) => {
          setShowMapPanel(true);
          setMapResults([result]);
        }}
      />
    ) : null}
    <CornerBackButton
      handleClick={() => {
        if (showMapPanel) setShowMapPanel(false);
        else setShowResultsPanel(false);
      }}
    />
  </div>
);

const lastBotUtterance = pipe(
  filter(propEq("type", eventTypes.BOT_UTTERANCE)),
  last
);

export default ({
  features,
  history,
  isThinking,
  isRecording,
  sendEvent,
  sendTextUtterance,
  sendUtteranceSuggestion,
  startRecording,
  stopRecording,
  transcribedText,
  onClose,
}) => {
  const [showMapPanel, setShowMapPanel] = useState(false);
  const [showResultsPanel, setShowResultsPanel] = useState(false);
  const [results, setResults] = useState([]);
  const [mapResults, setMapResults] = useState([]);
  const isLeftSideOpen = showResultsPanel || showMapPanel;
  const useStyles = createUseStyles(
    makeStylesFromThemeAndCustomStyles(makeStyles, features.customWidgetStyle)
  );
  const styles = useStyles();
  const {
    data: { hints, tags },
  } = lastBotUtterance(history) || {
    data: { hint: [], tags: [] },
  };
  return (
    <div
      className={[
        styles.widgetInterface,
        features.isFullScreen ? styles.fullScreen : styles.widgetInterface,
      ].join(" ")}
    >
      {isLeftSideOpen && (mapResults.length || results.length) ? (
        <LeftPanel
          styles={styles}
          mapResults={mapResults}
          results={results}
          sendEvent={sendEvent}
          sendUtteranceSuggestion={sendUtteranceSuggestion}
          setMapResults={setMapResults}
          setShowMapPanel={setShowMapPanel}
          setShowResultsPanel={setShowResultsPanel}
          showMapPanel={showMapPanel}
          showResultsPanel={showResultsPanel}
        />
      ) : null}
      <div
        className={[
          styles.rightSideWrapper,
          isLeftSideOpen ? styles.leftIsOpen : "",
        ].join(" ")}
        style={features.rightSideStyle || {}}
      >
        <Header
          closeWidget={onClose}
          isBeta={features.isBeta}
          title={features.title}
          headerLogo={features.headerLogo}
          topRibbonText={features.topRibbonText}
          customWidgetStyle={features.customWidgetStyle}
        />
        {tags.length ? <Tags tags={tags} /> : null}
        <Chat
          onResultsClick={(results) => {
            sendEvent(eventTypes.LIST_VIEW_CLICK);
            setResults(results);
            if (showMapPanel) {
              setShowMapPanel(false);
              setShowResultsPanel(true);
            } else {
              setShowResultsPanel(!showResultsPanel);
            }
          }}
          onMapClick={(results) => {
            sendEvent(eventTypes.MAP_CLICK);
            setMapResults(results);
            setShowMapPanel(true);
          }}
          messages={historyToMessages(history)}
          isThinking={isThinking}
          isAgent={false}
          transcribedText={isRecording && transcribedText}
          numberOfIterationsToShow={features.numberOfIterationsToShow}
          sendEvent={sendEvent}
          showAvatar={features.showAvatar}
          onSuggestionClick={handleSuggestionClick({
            sendEvent,
            sendUtteranceSuggestion,
          })}
          customAvatar={features.customAvatar}
          shouldTruncate={features.shouldTruncate}
          decorateOnHolidays={features.decorateOnHolidays}
          hyroLogo={features.hyroLogo}
          customWidgetStyle={features.customWidgetStyle}
          isFeedbackActivated={!features.disableFeedback}
        />
        <Footer
          textAreaPlaceholder={features.textAreaPlaceholder}
          sendTextQuery={sendTextUtterance}
          startRecording={startRecording}
          stopRecording={stopRecording}
          isRecording={isRecording}
          shouldBlock={history.some((action) => action.blacklist) || isThinking}
          hints={hints}
          customWidgetStyle={features.customWidgetStyle}
        />
      </div>
    </div>
  );
};

export { eventToMessages };
